import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Anchor, AlertTriangle, Ship } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const CHOKEPOINTS = [
  { id: "hormuz", name: "Strait of Hormuz", lat: 26.56, lng: 56.25, radius: 200, desc: "~21M bbl/day oil transit", criticality: "extreme" },
  { id: "suez", name: "Suez Canal", lat: 30.46, lng: 32.35, radius: 150, desc: "~12% global trade", criticality: "extreme" },
  { id: "bab", name: "Bab el-Mandeb", lat: 12.58, lng: 43.33, radius: 150, desc: "Red Sea gateway", criticality: "high" },
  { id: "malacca", name: "Strait of Malacca", lat: 2.5, lng: 101.5, radius: 200, desc: "~25% global trade", criticality: "extreme" },
  { id: "taiwan", name: "Taiwan Strait", lat: 24.5, lng: 119.5, radius: 200, desc: "Semiconductor supply line", criticality: "high" },
  { id: "gibraltar", name: "Strait of Gibraltar", lat: 35.96, lng: -5.5, radius: 100, desc: "Med–Atlantic gateway", criticality: "moderate" },
  { id: "bosporus", name: "Bosporus Strait", lat: 41.12, lng: 29.05, radius: 50, desc: "Black Sea access", criticality: "high" },
  { id: "panama", name: "Panama Canal", lat: 9.08, lng: -79.68, radius: 100, desc: "Pacific–Atlantic shortcut", criticality: "high" },
];

const critColors: Record<string, string> = {
  extreme: "text-red-400 border-red-500/30 bg-red-500/10",
  high: "text-orange-400 border-orange-500/30 bg-orange-500/10",
  moderate: "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
};

function haversine(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const SupplyChainMonitor = () => {
  const { data: eventsData } = useQuery({
    queryKey: ["all-events-supply"],
    queryFn: async () => {
      const since = new Date(Date.now() - 48 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("events")
        .select("lat, lng, event_type, title, occurred_at")
        .gte("occurred_at", since)
        .not("lat", "is", null)
        .limit(500);
      if (error) throw error;
      return data || [];
    },
    staleTime: 120000,
  });

  const events = eventsData || [];

  // Calculate disruption risk per chokepoint
  const chokepointData = CHOKEPOINTS.map((cp) => {
    const nearbyEvents = events.filter((e) => {
      if (!e.lat || !e.lng) return false;
      return haversine(cp.lat, cp.lng, e.lat, e.lng) <= cp.radius;
    });
    const riskScore = Math.min(100, nearbyEvents.length * 15 + (nearbyEvents.some((e) => e.event_type === "missile") ? 30 : 0));
    return { ...cp, nearbyEvents: nearbyEvents.length, riskScore };
  }).sort((a, b) => b.riskScore - a.riskScore);

  return (
    <section id="supply-chain" className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6 flex items-center gap-3">
          <Anchor className="h-5 w-5 text-primary" />
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
            Supply Chain & Trade Chokepoints
          </h2>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {chokepointData.map((cp) => (
            <div key={cp.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-bold text-foreground">{cp.name}</h3>
                <Badge variant="outline" className={`text-[8px] font-mono ${critColors[cp.criticality]}`}>
                  {cp.criticality.toUpperCase()}
                </Badge>
              </div>
              <p className="text-[10px] text-muted-foreground mb-3">{cp.desc}</p>

              {/* Risk bar */}
              <div className="mb-2">
                <div className="flex justify-between text-[9px] font-mono text-muted-foreground mb-1">
                  <span>Disruption Risk</span>
                  <span className={cp.riskScore > 50 ? "text-red-400" : cp.riskScore > 20 ? "text-orange-400" : "text-green-400"}>
                    {cp.riskScore}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${cp.riskScore > 50 ? "bg-red-500" : cp.riskScore > 20 ? "bg-orange-500" : "bg-green-500"}`}
                    style={{ width: `${cp.riskScore}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
                {cp.nearbyEvents > 0 ? (
                  <>
                    <AlertTriangle className="h-3 w-3 text-orange-400" />
                    {cp.nearbyEvents} events within {cp.radius}km (48h)
                  </>
                ) : (
                  <>
                    <Ship className="h-3 w-3 text-green-400" />
                    No nearby threats detected
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SupplyChainMonitor;
